import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const CompanyPublicPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl(`userprofiles/companies/${slug}/public-page/`));
        if (isMounted) {
          setPageData(response.data || null);
          setError('');
        }
      } catch (requestError) {
        if (isMounted) {
          setError(requestError?.response?.data?.error || 'Unable to load company page.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#F6F4F8] p-6"><InlineFeedback type="info" message="Loading company page..." /></div>;
  }

  if (error) {
    return <div className="min-h-screen bg-[#F6F4F8] p-6"><InlineFeedback type="error" message={error} /></div>;
  }

  const payload = pageData?.payload || {};
  const brand = payload?.brand || {};
  const hero = payload?.hero || {};
  const about = payload?.about || {};
  const contact = payload?.contact || {};
  const highlights = Array.isArray(payload?.highlights) ? payload.highlights : [];
  const testimonials = Array.isArray(payload?.testimonials) ? payload.testimonials : [];
  const gallery = Array.isArray(payload?.gallery) ? payload.gallery.filter((row) => row?.image_url) : [];
  const headerStyle = {
    backgroundColor: brand.primary_color || '#6D3B7E',
    backgroundImage: hero.hero_image_url
      ? `linear-gradient(120deg, rgba(75,42,86,.92), rgba(205,163,73,.76)), url("${hero.hero_image_url}")`
      : `linear-gradient(120deg, ${brand.primary_color || '#6D3B7E'}, ${brand.accent_color || '#CDA349'})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
  };

  return (
    <div className="min-h-screen bg-[#F6F4F8] text-[#2E2E2E]">
      <header
        className="px-3 py-7 text-white sm:px-4 sm:py-10"
        style={headerStyle}
      >
        <div className="mx-auto max-w-6xl">
          {brand.logo_url ? <img src={brand.logo_url} alt={`${pageData?.company?.name || 'Company'} logo`} className="mb-5 max-h-20 max-w-[min(240px,100%)] object-contain" /> : null}
          <p className="text-xs uppercase tracking-widest text-white/80">{brand.tagline || 'Beauty services'}</p>
          <h1 className="mt-2 break-words text-3xl font-bold sm:text-4xl">{pageData?.company?.name || 'Company'}</h1>
          <h2 className="mt-3 max-w-2xl text-xl font-semibold text-white sm:text-2xl">{hero.headline || 'Beauty appointments made simple'}</h2>
          <p className="mt-4 max-w-2xl text-sm text-white/90">{hero.subheadline || 'Book trusted professionals and discover your next favorite style.'}</p>
          <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
            <Link
              to="/booking"
              className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2E2E2E] hover:bg-gray-100"
              aria-label={`Book an appointment with ${pageData?.company?.name || 'this company'}`}
            >
              {hero.primary_cta_label || pageData?.booking_cta?.label || 'Book Appointment'}
            </Link>
            <Link to="/services" className="rounded-full bg-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/30">
              {hero.secondary_cta_label || 'View Services'}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-3 py-5 sm:space-y-6 sm:px-4 sm:py-8">
        <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <h2 className="text-2xl font-semibold text-[#4B2A56]">{about.title || 'About Our Company'}</h2>
          <p className="mt-3 text-sm text-gray-700">{about.body || 'Professional beauty services with clear scheduling and quality care.'}</p>
        </section>

        {highlights.length ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <h2 className="text-xl font-semibold text-[#4B2A56]">Why customers choose us</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {highlights.map((row, index) => (
                <article key={`${row.title || 'highlight'}-${index}`} className="rounded-xl border border-gray-200 bg-[#FCFBFE] p-4">
                  <h3 className="font-semibold text-[#4B2A56]">{row.title}</h3>
                  {row.description ? <p className="mt-2 text-sm leading-6 text-gray-600">{row.description}</p> : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <h2 className="text-xl font-semibold text-[#4B2A56]">Featured Services</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(pageData?.services || []).map((service) => (
              <article key={service.id} className="rounded-xl border border-gray-200 bg-[#FCFBFE] p-4">
                <h3 className="font-semibold text-[#4B2A56]">{service.name}</h3>
                <p className="mt-2 text-sm text-gray-600">{service.description || 'Professional beauty service.'}</p>
                <p className="mt-2 text-sm font-semibold text-[#2E2E2E]">${service.price}</p>
              </article>
            ))}
          </div>
          {(pageData?.services || []).length === 0 ? <p className="mt-3 text-sm text-gray-500">No services published yet.</p> : null}
        </section>

        {gallery.length ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <h2 className="text-xl font-semibold text-[#4B2A56]">Our work</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {gallery.map((row, index) => (
                <img key={`${row.image_url}-${index}`} src={row.image_url} alt={row.alt || `${pageData?.company?.name || 'Company'} gallery photo ${index + 1}`} className="aspect-square w-full rounded-xl object-cover" loading="lazy" />
              ))}
            </div>
          </section>
        ) : null}

        {testimonials.length ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <h2 className="text-xl font-semibold text-[#4B2A56]">What customers say</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {testimonials.map((row, index) => (
                <blockquote key={`${row.author || 'customer'}-${index}`} className="rounded-xl border border-gray-200 bg-[#FCFBFE] p-4">
                  <p className="text-sm italic leading-6 text-gray-700">“{row.quote}”</p>
                  <footer className="mt-3 text-sm font-semibold text-[#4B2A56]">— {row.author}</footer>
                </blockquote>
              ))}
            </div>
          </section>
        ) : null}

        {(contact.phone || contact.email || contact.address_line) ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <h2 className="text-xl font-semibold text-[#4B2A56]">Contact us</h2>
            <div className="mt-3 space-y-2 text-sm text-gray-700">
              {contact.phone ? <p><strong>Phone:</strong> <a className="text-[#6D3B7E] underline" href={`tel:${contact.phone}`}>{contact.phone}</a></p> : null}
              {contact.email ? <p><strong>Email:</strong> <a className="text-[#6D3B7E] underline" href={`mailto:${contact.email}`}>{contact.email}</a></p> : null}
              {contact.address_line ? <p><strong>Address:</strong> {contact.address_line}</p> : null}
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
};

export default CompanyPublicPage;
